import React, { useState } from 'react';
import styles from "./Potable.module.css";
import { formateDate } from '../../../../Website/utils/utils';

const SELECTABLE = ["PENDING", "APPROVED", "RESCHEDULE", "AWAITING"];

const th = { padding: '12px 14px', fontSize: '11.5px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#64748b', textAlign: 'left', background: '#f8fafc', whiteSpace: 'nowrap', borderBottom: '1px solid #e2e8f0' };
const td = { padding: '12px 14px', fontSize: '13.5px', color: '#334155', verticalAlign: 'middle', borderBottom: '1px solid #eef2f7' };
const num = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', fontWeight: 600, color: '#0f172a' };

const Pill = ({ children, tone = 'grey' }) => {
    const tones = {
        grey: { background: '#F1F5F9', color: '#475569', border: '#E2E8F0' },
        amber: { background: '#FFFBEB', color: '#B45309', border: '#FDE68A' },
        green: { background: '#ECFDF5', color: '#047857', border: '#A7F3D0' },
        purple: { background: '#F4F3FF', color: '#512DA8', border: '#DDD6FE' },
    }[tone];
    return (
        <span style={{ display: 'inline-block', padding: '2px 9px', borderRadius: 999, fontSize: 11.5, fontWeight: 600, whiteSpace: 'nowrap', background: tones.background, color: tones.color, border: `1px solid ${tones.border}` }}>
            {children}
        </span>
    );
};

// Product photo, or a plain chip placeholder when there is no photo (same as the excess inventory table)
const ProductImage = ({ src }) => {
    const [failed, setFailed] = useState(false);
    return (
        <div style={{ width: 40, height: 40, borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0, padding: 3 }}>
            {(!src || failed) ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="6" y="6" width="12" height="12" rx="2" />
                    <path d="M9 2v4M15 2v4M9 18v4M15 18v4M2 9h4M2 15h4M18 9h4M18 15h4" />
                </svg>
            ) : (
                <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} onError={() => setFailed(true)} />
            )}
        </div>
    );
};

// Shipment state of one line, from what is still pending
const lineState = (item) => {
    const total = Number(item?.totalQty) || 0;
    const pending = Number(item?.pendingOrderCount);
    if (!total || Number.isNaN(pending)) return { label: 'Pending', tone: 'grey' };
    if (pending <= 0) return { label: 'Delivered', tone: 'green' };
    if (pending < total) return { label: `${(total - pending).toLocaleString('en-IN')} of ${total.toLocaleString('en-IN')} shipped`, tone: 'amber' };
    return { label: 'Pending', tone: 'grey' };
};

const PoTable = ({ data, orderDetails, setSelectedItems, selectedItems, status }) => {
    const canSelect = SELECTABLE.includes(status);

    const toggleSelection = (item) => {
        setSelectedItems(prevSelected => {
            const isAlreadySelected = prevSelected?.some(selected => selected._id === item._id);
            return isAlreadySelected
                ? prevSelected?.filter(selected => selected._id !== item._id)
                : [...prevSelected, item];
        });
    };
    const isSelected = (item) => selectedItems?.some(selected => selected._id === item._id);

    const selectable = (data || []).filter((i) => i?.rescheduled !== true);
    const allSelected = canSelect && selectable.length > 0 && selectable.every(isSelected);
    const toggleAll = () => setSelectedItems(allSelected ? [] : selectable);

    return (
        <div style={{ width: '100%', overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 12, background: '#fff' }}>
            <table className={styles.previewtable} style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
                <thead>
                    <tr>
                        <th style={{ ...th, width: 36, padding: '12px 10px 12px 16px' }}>
                            {canSelect && selectable.length > 0 && (
                                <input type="checkbox" checked={allSelected} onChange={toggleAll} title="Select all" style={{ width: 15, height: 15, cursor: 'pointer', accentColor: '#512DA8', margin: 0, verticalAlign: 'middle' }} />
                            )}
                        </th>
                        <th style={th}>Part #</th>
                        <th style={th}>Manufacturer</th>
                        <th style={{ ...th, textAlign: 'right' }}>Total qty</th>
                        <th style={{ ...th, textAlign: 'right' }}>Pending</th>
                        <th style={{ ...th, textAlign: 'right' }}>PO price</th>
                        <th style={th}>Schedule date</th>
                        <th style={th}>Status</th>
                    </tr>
                </thead>
                <tbody>
                    {(data || []).map((item) => {
                        const rescheduled = item?.rescheduled === true;
                        const state = lineState(item);
                        const partNumber = item?.productId?.productOverview?.productNumber?.[0] || item?.productNumber || 'Part Item';
                        const manufacturer = item?.productId?.productOverview?.manufacturer?.[0]?.name || item?.manufacturerName || '-';
                        return (
                            <tr key={item._id || item.id} className={isSelected(item) ? styles.selectedRow : ''} style={{ background: rescheduled ? '#FBFAFF' : '#fff' }}>
                                <td style={{ ...td, padding: '12px 10px 12px 16px' }}>
                                    {canSelect && !rescheduled && (
                                        <input
                                            type="checkbox"
                                            checked={isSelected(item)}
                                            onChange={() => toggleSelection(item)}
                                            style={{ width: 15, height: 15, cursor: 'pointer', accentColor: '#512DA8', margin: 0, verticalAlign: 'middle' }}
                                        />
                                    )}
                                </td>
                                <td style={td}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <ProductImage src={item?.productId?.assets?.image?.[0]} />
                                        <span style={{ color: '#0f172a', fontWeight: 600, overflowWrap: 'anywhere' }}>{partNumber}</span>
                                    </div>
                                </td>
                                <td style={td}>{manufacturer}</td>
                                <td style={num}>{Number(item?.totalQty || 0).toLocaleString('en-IN')}</td>
                                <td style={num}>{Number(item?.pendingOrderCount || 0).toLocaleString('en-IN')}</td>
                                <td style={{ ...num, color: '#512DA8' }}>₹{item?.unitPrice}</td>
                                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                    {rescheduled && item?.rescheduleDate ? (
                                        <>
                                            <div style={{ color: '#512DA8', fontWeight: 700 }}>{formateDate(item.rescheduleDate)}</div>
                                            {item?.deliveryDate && <div style={{ fontSize: 11, color: '#94a3b8' }}>was {formateDate(item.deliveryDate)}</div>}
                                        </>
                                    ) : (
                                        <>
                                            <div style={{ color: '#0f172a', fontWeight: 500 }}>{item?.deliveryDate ? formateDate(item.deliveryDate) : '-'}</div>
                                            {item?.lastDeliveryDate && <div style={{ fontSize: 11, color: '#94a3b8' }}>last dispatch {formateDate(item.lastDeliveryDate)}</div>}
                                        </>
                                    )}
                                </td>
                                <td style={td}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                                        <Pill tone={state.tone}>{state.label}</Pill>
                                        {rescheduled && (
                                            <Pill tone="purple">Rescheduled · {Number(item?.rescheduledQuantity || 0).toLocaleString('en-IN')} pcs</Pill>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default PoTable;
